import React, { useEffect, useState } from 'react';
import { FormattedMarkdownDisplay } from '@/common/components/course';

interface Props {
  pdfBlob: Blob | null;
  markdown?: string | null;
  mimeType?: string;
  name?: string;
}

const DocumentDisplay = ({ pdfBlob, markdown, mimeType, name }: Props) => {
  const [blobURL, setBlobURL] = useState<string | undefined>();
  const [resetViewer, setResetViewer] = useState<boolean>(false);

  useEffect(() => {
    let url: string | undefined;

    if (pdfBlob) {
      if (
        mimeType == 'application/pdf' ||
        mimeType == 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        mimeType == 'application/msword' ||
        mimeType == 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
        mimeType == 'application/vnd.ms-powerpoint' ||
        mimeType == 'text/plain'
      ) {
        url = URL.createObjectURL(pdfBlob);
        setBlobURL(url);

        // Auto-initiate download for non-displayable documents
        if (mimeType != 'application/pdf') {
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `${name ?? 'document'}.${getFileExtension()}`);

          document.body.appendChild(link);
          link.click();

          // Clean up and remove the link
          document.body.removeChild(link);
        }

        // Clear viewer before loading new content
        setResetViewer(true);
      } else {
        setBlobURL('Invalid type');
      }
    }

    return () => {
      // Only revoke if a URL was created
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [pdfBlob, mimeType]);

  useEffect(() => {
    if (resetViewer) {
      // Allow viewer to reinitialize
      setTimeout(() => setResetViewer(false), 0);
    }
  }, [resetViewer]);

  const getFileExtension = () => {
    if (mimeType == 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')
      return 'docx';
    if (mimeType == 'application/msword') return 'doc';
    if (mimeType == 'application/vnd.openxmlformats-officedocument.presentationml.presentation')
      return 'pptx';
    if (mimeType == 'application/vnd.ms-powerpoint') return 'ppt';
    if (mimeType == 'text/plain') return 'txt';
    return '';
  };

  if (resetViewer) {
    return <div>Loading...</div>;
  }

  if (mimeType === 'text/markdown' && markdown) {
    return (
      <div className='w-full h-full overflow-y-auto'>
        <FormattedMarkdownDisplay documentContent={markdown ?? ''} />
      </div>
    );
  }

  return blobURL ? (
    <>
      {mimeType == 'application/pdf' && (
        <object data={blobURL} type={mimeType ?? 'application/pdf'} width='100%' height='100%' />
      )}
      {(mimeType == 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        mimeType == 'application/msword' ||
        mimeType == 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
        mimeType == 'application/vnd.ms-powerpoint' ||
        mimeType == 'text/plain') && (
        <a
          href={blobURL}
          download={`${name ?? 'document'}.${getFileExtension()}`}
          className='mt-8 mx-auto underline'
        >
          Document download should start automatically. Otherwise, click here
        </a>
      )}
      {blobURL == 'Invalid type' && <p>Something went wrong. Invalid document found.</p>}
    </>
  ) : (
    <div>Loading...</div>
  );
};

export default DocumentDisplay;
